import React from "react";

import { Box, Drawer, Stack, useTheme } from "@mui/material";
import { ApprovePlanConfirmationDialog } from "@src/components/MainApp/Alerts/ApprovePlanConfirmationDialog";
import { NavDrawerNudge } from "@src/components/MainApp/Alerts/NavDrawerNudge";
import { AdminSection } from "@src/components/Navigation/BaseSideNav/AdminSection";
import { StoreManagementSection } from "@src/components/Navigation/BaseSideNav/StoreManagerSection";
import { TopNavSection } from "@src/components/Navigation/BaseSideNav/TopNavSection";
import { useBillingAlertInset } from "@src/components/Navigation/BaseSideNav/useBillingAlertInset";
import { OrgPicker } from "@src/components/Navigation/OrgPicker/OrgPicker";
import { useSelector } from "@src/store";

const DRAWER_WIDTH_OPEN = 250;
const DRAWER_WIDTH_COLLAPSED = 64;

interface NavigationDrawerProps {
  isUnauthorized: boolean;
  hideOrgPicker?: boolean;
  settingsPage?: boolean;
  customFooter?: React.ReactNode;
  customBody?: React.ReactNode;
}

export const NavigationDrawer = ({
  isUnauthorized,
  customBody,
  customFooter,
  hideOrgPicker,
  settingsPage,
}: NavigationDrawerProps) => {
  const { inset, bottomInset } = useBillingAlertInset();

  const drawerOpenFromStore = useSelector(
    (state) => !state.appSettings.sideNavCollapsed
  );
  const theme = useTheme();

  const drawerOpen = settingsPage ? true : drawerOpenFromStore;
  const drawerWidth = drawerOpen ? DRAWER_WIDTH_OPEN : DRAWER_WIDTH_COLLAPSED;

  const widthTransition = theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: drawerOpen
      ? theme.transitions.duration.enteringScreen
      : theme.transitions.duration.leavingScreen,
  });

  return (
    <Drawer
      sx={{
        flexShrink: 0,
        overflowX: "hidden",
        width: drawerWidth,
        height: "100%",
        transition: widthTransition,
        "& .MuiDrawer-paper": {
          position: "relative",
          width: drawerWidth,
          height: "100%",
          boxSizing: "border-box",
          overflowX: "hidden",
          backgroundColor: settingsPage
            ? theme.igBackgroundColors.baseLightHigh
            : theme.igBackgroundColors.baseLightLow,
          border: "none",
          borderRadius: 0,
          borderRight: `1px solid ${theme.igBorders.core.default} !important`,
          transition: widthTransition,
        },
      }}
      variant="persistent"
      anchor="left"
      open={true}
    >
      <Stack
        sx={{
          position: "relative",
          width: drawerWidth,
          height: "100%",
        }}
      >
        <OrgPicker
          settingsPage={settingsPage}
          hidePicker={hideOrgPicker}
          drawerOpen={drawerOpen}
          hideCollapseControl={settingsPage}
        />
        {isUnauthorized ? null : customBody ? (
          customBody
        ) : (
          <Stack direction="column" sx={{ height: "100%", flex: 1 }}>
            <TopNavSection drawerOpen={drawerOpen} />
            <StoreManagementSection drawerOpen={drawerOpen} />
            <AdminSection drawerOpen={drawerOpen} />
            {customFooter !== undefined ? customFooter : null}
          </Stack>
        )}

        {drawerOpen && !settingsPage && (
          <Box
            sx={{
              position: "absolute",
              bottom: bottomInset,
              left: inset,
              right: inset,
              zIndex: 1,
            }}
          >
            <NavDrawerNudge />
          </Box>
        )}
      </Stack>
      <ApprovePlanConfirmationDialog />
    </Drawer>
  );
};
